import * as React from 'react';
import { useOptionLayout, OptionLayout } from './useOptionLayout';
import { useFlyoutLevelState, FlyoutLevel } from './useFlyoutLevelState';
import { OptionCategory, OptionSubCategory } from 'owa-options-core';
import { showFullOptions, confirmDirtyOptionsAndPerformAction } from 'owa-options-orchestration';
import { getCurrentCategory } from 'owa-options-store';

export interface FlyoutState {
    onCategorySelect?(category: OptionCategory): void;
    onSubCategorySelect?(subcategory: OptionSubCategory): void;
    onNavigateBackFromSubcategories?(): void;
    onSoftDismiss?(): void;
    onOpenFlyout?(): void;
    flyoutLevel: FlyoutLevel;
    flyoutCategory?: OptionCategory;
}

export function useFlyoutState(): FlyoutState {
    const [flyoutLevel, setFlyoutLevel] = useFlyoutLevelState();
    const layout = useOptionLayout();

    // Used outside of ThreeColumn
    // There, we don't want to navigate the underlying settings pane until a user selects a subcategory combo
    const [flyoutCategory, setFlyoutCategory] = React.useState<OptionCategory | undefined>();

    const onSoftDismiss = () => setFlyoutLevel(FlyoutLevel.Closed);
    const onSubCategorySelect = (subcategory: OptionSubCategory) => {
        // Set app state
        showFullOptions(flyoutCategory?.key, subcategory.key);

        // Reset local state
        setFlyoutCategory(undefined);
        setFlyoutLevel(FlyoutLevel.Closed);
    };

    switch (layout) {
        case OptionLayout.ThreeColumn:
            return { flyoutLevel };
        case OptionLayout.TwoColumn:
            return {
                // We only allow closed or Subcategories
                flyoutLevel,
                onCategorySelect(category: OptionCategory) {
                    confirmDirtyOptionsAndPerformAction(() => {
                        setFlyoutCategory(category);
                        setFlyoutLevel(FlyoutLevel.Subcategories);
                    });
                },
                onSubCategorySelect,
                flyoutCategory,
                onSoftDismiss,
                onOpenFlyout() {
                    confirmDirtyOptionsAndPerformAction(() => {
                        setFlyoutCategory(getCurrentCategory());
                        setFlyoutLevel(FlyoutLevel.Subcategories);
                    });
                },
            };
        case OptionLayout.OneColumn:
            return {
                // We only allow closed or SubcategoriesAndCategories
                flyoutLevel,
                onCategorySelect(category: OptionCategory) {
                    setFlyoutCategory(category);
                },
                flyoutCategory,
                onSubCategorySelect,
                onSoftDismiss,
                onOpenFlyout() {
                    confirmDirtyOptionsAndPerformAction(() => {
                        setFlyoutCategory(getCurrentCategory());
                        setFlyoutLevel(FlyoutLevel.SubcategoriesAndCategories);
                    });
                },
            };
        case OptionLayout.OneColumnNarrow:
            return {
                // We do not allow SubcategoriesAndCategories
                flyoutLevel,
                flyoutCategory,
                onCategorySelect(category: OptionCategory) {
                    setFlyoutCategory(category);
                    setFlyoutLevel(FlyoutLevel.Subcategories);
                },
                onSubCategorySelect,
                onSoftDismiss,
                onOpenFlyout() {
                    confirmDirtyOptionsAndPerformAction(() => {
                        setFlyoutLevel(FlyoutLevel.Subcategories);
                        setFlyoutCategory(getCurrentCategory());
                    });
                },
                onNavigateBackFromSubcategories() {
                    setFlyoutLevel(FlyoutLevel.Categories);
                },
            };
    }
}
